import { colormaps } from './colormaps';

const tilesBaseUrl = `${import.meta.env.VITE_NWDC_S3_URL}/tiles`;

// Common base layer type
interface BaseAdditionalLayer {
  active: boolean;
  id: string;
  name: string;
  opacity: number;
  layerType: 'tile' | 'geojson';
  url: string;
  sourceUrl?: string;
  sourceName?: string;
  minZoom?: number;
}

export enum ServiceType {
  ImageServer = 'imageServer',
  WMS = 'wms',
  WMTS = 'wmts',
  FeatureServerPbf = 'featureServerPbf',
}

// ImageServer type
export interface ImageServerAdditionalLayer extends BaseAdditionalLayer {
  serviceType: ServiceType.ImageServer;
  colormap?: Record<string, string>;
}

// WMS (1.3.0) type
export interface WMSAdditionalLayer extends BaseAdditionalLayer {
  serviceType: ServiceType.WMS;
  wmsLayerName: string;
  colormap?: Record<string, string>;
}

export enum GeomType {
  Polygon = 'polygon',
  Point = 'point',
  Line = 'line',
}

// MVTLayer for any future layers with vector data
export interface MVTAdditionalLayer extends BaseAdditionalLayer {
  borderColor?: string;
  borderWidth?: number;
  pointColor?: string;
  pointSize?: number;
  serviceType: ServiceType.WMTS;
  sourceLayer: string;
  geomType: GeomType;
  filled: boolean;
  colormap?: Record<string, string>;
  colormapFeatureKey?: string;
}

// GeoJsonLayer for any future layers with GeoJSON data
export interface GeoJsonAdditionalLayer extends BaseAdditionalLayer {
  serviceType: ServiceType.FeatureServerPbf; // add other service types as needed for GeoJsonLayers
  lineColor?: string;
  lineWidth?: number;
  geomType: GeomType;
  filled: boolean;
  borderColor?: string;
  borderWidth?: number;
  pointColor?: string;
  pointSize?: number;
}
export type AdditionalLayer =
  | ImageServerAdditionalLayer
  | WMSAdditionalLayer
  | MVTAdditionalLayer
  | GeoJsonAdditionalLayer;

export function isMVTAdditionalLayer(
  layer: AdditionalLayer
): layer is MVTAdditionalLayer {
  return (
    layer &&
    typeof layer === 'object' &&
    layer.serviceType === ServiceType.WMTS &&
    typeof layer.active === 'boolean' &&
    typeof layer.id === 'string' &&
    typeof layer.name === 'string' &&
    typeof layer.opacity === 'number' &&
    layer.layerType === 'tile' &&
    typeof layer.url === 'string' &&
    typeof layer.geomType === 'string' &&
    typeof layer.filled === 'boolean'
  );
}

export function isGeoJsonAdditionalLayer(
  layer: AdditionalLayer
): layer is GeoJsonAdditionalLayer {
  return (
    layer &&
    typeof layer === 'object' &&
    layer.serviceType === ServiceType.FeatureServerPbf &&
    typeof layer.active === 'boolean' &&
    typeof layer.id === 'string' &&
    typeof layer.name === 'string' &&
    typeof layer.opacity === 'number' &&
    layer.layerType === 'geojson' &&
    typeof layer.url === 'string' &&
    typeof layer.geomType === 'string' &&
    typeof layer.filled === 'boolean'
  );
}

// default colors used across group layers
export const watershedBorderColor = 'be309d'; // color matches National Water Dashboard
export const defaultPointColor = '000000'; // default point color for point layers
export const defaultBorderColor = '000000'; // default border color for polygon layers
export const defaultLineColor = '000000'; // default line color for line layers

// other default values
export const defaultBorderWidth = 1; // default border width for polygon layers
export const defaultPointSize = 5; // default point size for point layers
export const defaultLineWidth = 2; // default line width for line layers

type AdditionalLayers = Record<string, AdditionalLayer>;

const cropland: ImageServerAdditionalLayer = {
  active: false,
  id: 'cropland',
  layerType: 'tile',
  name: 'Cropland',
  opacity: 100,
  serviceType: ServiceType.ImageServer,
  colormap: colormaps.cropland,
  url: 'https://pdi.scinet.usda.gov/image/rest/services/CDL_WM/ImageServer',
  sourceUrl: 'https://catalog.data.gov/dataset/cropscape-cropland-data-layer',
  sourceName: 'USDA Cropland Data Layer',
};

const nlcd: WMSAdditionalLayer = {
  active: false,
  id: 'nlcd',
  name: 'Land Cover',
  opacity: 100,
  layerType: 'tile',
  serviceType: ServiceType.WMS,
  colormap: colormaps.nlcd,
  url: 'https://dmsdata.cr.usgs.gov/geoserver/mrlc_Land-Cover-Native_conus_year_data/wms',
  wmsLayerName: 'Land-Cover-Native_conus_year_data',
  sourceUrl:
    'https://www.usgs.gov/centers/eros/science/annual-nlcd-data-access',
  sourceName: 'USGS National Land Cover Database',
};

const huc2: MVTAdditionalLayer = {
  id: 'huc2',
  name: 'HUC2',
  active: false,
  borderColor: watershedBorderColor,
  borderWidth: 3,
  opacity: 100,
  url: `${tilesBaseUrl}/WBDHU2/{z}/{x}/{y}.pbf`,
  layerType: 'tile',
  serviceType: ServiceType.WMTS,
  sourceLayer: 'WBDHU2',
  geomType: GeomType.Polygon,
  filled: false,
};

const huc4: MVTAdditionalLayer = {
  id: 'huc4',
  name: 'HUC4',
  active: false,
  borderColor: watershedBorderColor,
  borderWidth: 3,
  opacity: 100,
  url: `${tilesBaseUrl}/WBDHU4/{z}/{x}/{y}.pbf`,
  layerType: 'tile',
  serviceType: ServiceType.WMTS,
  sourceLayer: 'WBDHU4',
  geomType: GeomType.Polygon,
  filled: false,
};
const huc6: MVTAdditionalLayer = {
  id: 'huc6',
  name: 'HUC6',
  active: false,
  borderColor: watershedBorderColor,
  borderWidth: 3,
  opacity: 100,
  url: `${tilesBaseUrl}/WBDHU6/{z}/{x}/{y}.pbf`,
  layerType: 'tile',
  serviceType: ServiceType.WMTS,
  sourceLayer: 'WBDHU6',
  geomType: GeomType.Polygon,
  filled: false,
};
const huc8: MVTAdditionalLayer = {
  id: 'huc8',
  name: 'HUC8',
  active: false,
  borderColor: watershedBorderColor,
  borderWidth: 3,
  opacity: 100,
  url: `${tilesBaseUrl}/WBDHU8/{z}/{x}/{y}.pbf`,
  layerType: 'tile',
  serviceType: ServiceType.WMTS,
  sourceLayer: 'WBDHU8',
  geomType: GeomType.Polygon,
  filled: false,
};
const huc10: MVTAdditionalLayer = {
  id: 'huc10',
  name: 'HUC10',
  active: false,
  borderColor: watershedBorderColor,
  borderWidth: 3,
  opacity: 100,
  url: `${tilesBaseUrl}/WBDHU10/{z}/{x}/{y}.pbf`,
  layerType: 'tile',
  serviceType: ServiceType.WMTS,
  sourceLayer: 'WBDHU10',
  geomType: GeomType.Polygon,
  filled: false,
};
const counties: MVTAdditionalLayer = {
  id: 'counties',
  name: 'Counties',
  active: false,
  borderColor: '222222',
  borderWidth: 1,
  opacity: 100,
  url: `${tilesBaseUrl}/counties/{z}/{x}/{y}.pbf`,
  layerType: 'tile',
  serviceType: ServiceType.WMTS,
  sourceLayer: 'counties',
  geomType: GeomType.Polygon,
  filled: false,
  sourceUrl:
    'https://www.census.gov/geographies/mapping-files/time-series/geo/tiger-line-file.html',
  sourceName: 'US Census Bureau TIGER Data',
};


const states: MVTAdditionalLayer = {
  id: 'states',
  name: 'States',
  active: false,
  borderColor: '222222',
  borderWidth: 1,
  opacity: 100,
  url: `${tilesBaseUrl}/states/{z}/{x}/{y}.pbf`,
  layerType: 'tile',
  serviceType: ServiceType.WMTS,
  sourceLayer: 'states',
  geomType: GeomType.Polygon,
  filled: false,
  sourceUrl:
    'https://www.census.gov/geographies/mapping-files/time-series/geo/tiger-line-file.html',
  sourceName: 'US Census Bureau TIGER Data',
};
const aquifers: MVTAdditionalLayer = {
  id: 'aquifers',
  name: 'Aquifers',
  active: false,
  borderColor: defaultBorderColor,
  borderWidth: 0,
  colormap: colormaps.aquifers,
  colormapFeatureKey: 'ROCK_NAME',
  opacity: 65,
  url: `${tilesBaseUrl}/us_aquifers/{z}/{x}/{y}.pbf`,
  layerType: 'tile',
  serviceType: ServiceType.WMTS,
  sourceLayer: 'us_aquifers',
  geomType: GeomType.Polygon,
  filled: true,
  sourceUrl:
    'https://www.usgs.gov/mission-areas/water-resources/science/principal-aquifers-united-states',
  sourceName: 'USGS Principal Aquifers of the US',
};
const wastewaterTreatmentPlants: MVTAdditionalLayer = {
  id: 'wastewaterTreatmentPlants',
  name: 'Wastewater Treatment Plants',
  active: false,
  opacity: 100,
  url: `${tilesBaseUrl}/hydrowaste/{z}/{x}/{y}.pbf`,
  layerType: 'tile',
  serviceType: ServiceType.WMTS,
  sourceLayer: 'hydrowaste',
  geomType: GeomType.Point,
  filled: true,
  pointSize: 3,
  pointColor: '7B3F00',
  sourceUrl: 'https://www.hydrosheds.org/products/hydrowaste',
  sourceName: 'HydroWASTE',
};

const nri: GeoJsonAdditionalLayer = {
  id: 'nri',
  name: 'Nationwide Rivers Inventory',
  active: false,
  lineColor: '006884',
  lineWidth: 3,
  opacity: 100,
  url: `https://services1.arcgis.com/fBc8EJBxQRMcHlei/arcgis/rest/services
/Nationwide_Rivers_Inventory_Official/FeatureServer/0/query?where=1=1
&outFields=GNIS_Name,NRI_ID&geometryType=esriGeometryEnvelope&inSR=4326
&spatialRel=esriSpatialRelIntersects&outSR=4326`,
  layerType: 'geojson',
  serviceType: ServiceType.FeatureServerPbf,
  geomType: GeomType.Line,
  filled: false,
  sourceUrl:
    'https://public-nps.opendata.arcgis.com/datasets/nps::nationwide-rivers-inventory-official-1/about',
  sourceName: 'NPS Nationwide Rivers Inventory',
  minZoom: 7  
};

const nhdNetworkFlowline: GeoJsonAdditionalLayer = {
  id: 'nhdNetworkFlowline',
  name: 'Rivers',
  active: false,
  opacity: 100,
  lineColor: '4169E1',
  lineWidth: 3,
  layerType: 'geojson',
  serviceType: ServiceType.FeatureServerPbf,
  url: 'https://services.arcgis.com/P3ePLMYs2RVChkJx/arcgis/rest/services/USA_Rivers_and_Streams/FeatureServer/0/query?where=1=1&outFields=Name&geometryType=esriGeometryEnvelope&inSR=4326&spatialRel=esriSpatialRelIntersects&outSR=4326&outFields=Name',
  sourceUrl:
    'https://hydro.nationalmap.gov/arcgis/rest/services/nhd/MapServer/6',
  sourceName: 'USGS National Hydrography Dataset',
  geomType: GeomType.Line,
  filled: false,
  minZoom: 10
};

const reservoirs: GeoJsonAdditionalLayer = {
  id: 'reservoirs',
  name: 'Reservoirs',
  active: false,
  opacity: 100,
  borderColor: '4169E1',
  borderWidth: 3,
  filled: true,
  layerType: 'geojson',
  serviceType: ServiceType.FeatureServerPbf,
  // URL for the USA Detailed Water Bodies, filtering by NAME containing 'Reservoir' or FTYPE containing reservoir code
  url: `https://services.arcgis.com/P3ePLMYs2RVChkJx/arcgis/rest/services/USA_Detailed_Water_Bodies/FeatureServer/0/query?where=UPPER(NAME) LIKE '%RESERVOIR%' OR UPPER(FTYPE) LIKE '%RESERVOIR%'&outFields=NAME&geometryType=esriGeometryEnvelope&inSR=4326&spatialRel=esriSpatialRelIntersects&outSR=4326`,
  geomType: GeomType.Polygon,
  sourceUrl:
    'https://hydro.nationalmap.gov/arcgis/rest/services/nhd/MapServer/12',
  sourceName: 'USGS National Hydrography Dataset',
  minZoom: 7
};

// // need for Vite proxy
// const damsBaseUrl = import.meta.env.VITE_LOCAL_DEV
//   ? '/usace-nid/dls/rest/services/NID/National_Inventory_of_Dams_Public_Service/FeatureServer/0/query'
//   : 'https://geospatial.sec.usace.army.mil/dls/rest/services/NID/National_Inventory_of_Dams_Public_Service/FeatureServer/0/query';

// const dams: GeoJsonAdditionalLayer = {
//   id: 'dams',
//   name: 'Dams',
//   active: false,
//   pointColor: '666666',
//   pointSize: 3,
//   opacity: 100,
//   url: `${damsBaseUrl}?where=1=1&outFields=OBJECTID&geometryType=esriGeometryEnvelope&inSR=4326&spatialRel=esriSpatialRelIntersects&outSR=4326`,
//   layerType: 'geojson',
//   serviceType: ServiceType.FeatureServerPbf,
//   geomType: GeomType.Point,
//   filled: false,
//   sourceUrl:
//     'https://hydro.nationalmap.gov/arcgis/rest/services/nhd/MapServer/0',
//   sourceName: 'USGS National Hydrography Dataset',
//   minZoom: 8
// };

const railroads: GeoJsonAdditionalLayer = {
  id: 'railroads',
  name: 'Railroads',
  active: false,
  opacity: 100,
  lineColor: defaultLineColor,
  lineWidth: 3,
  layerType: 'geojson',
  serviceType: ServiceType.FeatureServerPbf,
  // URL for the North American Rail Network Lines, filtering by NET values for active rail lines
  url: `https://services.arcgis.com/xOi1kZaI0eWDREZv/arcgis/rest/services/NTAD_North_American_Rail_Network_Lines/FeatureServer/0/query?where=NET IN ('F','I','M','O','S','Y')&outFields=NET&geometryType=esriGeometryEnvelope&inSR=4326&spatialRel=esriSpatialRelIntersects&outSR=4326`,
  geomType: GeomType.Line,
  filled: false,
  sourceUrl:
    'https://geodata.bts.gov/datasets/usdot::north-american-rail-network-lines/about',
  sourceName: 'BTS North American Rail Network Lines',
  minZoom: 9
};
export const additionalLayers: AdditionalLayers = {
  cropland,
  nlcd,
  huc2,
  huc4,
  huc6,
  huc8,
  huc10,
  counties,
  states,
  aquifers,
  wastewaterTreatmentPlants,
  nri,
  nhdNetworkFlowline,
  reservoirs,
  railroads,
  // dams,
};
